import { Check, ChevronDown, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { levenshteinDistance } from "src/common/utils";
import { Input } from "src/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

import type { GithubRepo } from "../types";

interface GithubRepoTypeaheadProps {
  repos: GithubRepo[];
  value: string;
  onChange: (repoId: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
}

interface RepoResultsListProps {
  repos: GithubRepo[];
  selectedRepoId: string;
  onSelect: (repo: GithubRepo) => void;
  emptyMessage: string;
}

const RepoResultsList = React.memo<RepoResultsListProps>(function RepoResultsList({
  repos,
  selectedRepoId,
  onSelect,
  emptyMessage,
}) {
  if (repos.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="p-0.5">
      {repos.map((repo) => (
        <button
          key={repo.id}
          onClick={() => onSelect(repo)}
          className={`
            w-full flex items-center justify-between px-2 py-1.5 text-xs rounded
            hover:bg-blue-950/40 transition-colors text-left
            ${selectedRepoId === repo.id.toString() ? "bg-card" : ""}
          `}
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="truncate text-foreground">{repo.full_name}</span>
            <span className="text-xs text-muted-foreground shrink-0">({repo.default_branch})</span>
          </div>
          {selectedRepoId === repo.id.toString() && (
            <Check className="h-3 w-3 text-primary shrink-0" />
          )}
        </button>
      ))}
    </div>
  );
});

export function GithubRepoTypeahead({
  repos,
  value,
  onChange,
  isLoading,
  disabled = false,
  placeholder = "Select a repository...",
  emptyMessage = "No repositories found matching your search.",
}: GithubRepoTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredRepos = useMemo(() => {
    if (!searchQuery) {
      // If no search query, show selected repo first if it exists
      if (value) {
        const selected = repos.find((r) => r.id.toString() === value);
        if (selected) {
          const others = repos.filter((r) => r.id.toString() !== value);
          return [selected, ...others.sort((a, b) => a.full_name.localeCompare(b.full_name))];
        }
      }
      return repos.slice().sort((a, b) => a.full_name.localeCompare(b.full_name));
    }

    const query = searchQuery.toLowerCase();

    // Filter repos that contain the query
    const matchingRepos = repos.filter((r) =>
      r.full_name.toLowerCase().includes(query) || r.name.toLowerCase().includes(query)
    );

    // Sort by edit distance
    return matchingRepos.sort((a, b) => {
      const distanceA = levenshteinDistance(query, a.full_name.toLowerCase());
      const distanceB = levenshteinDistance(query, b.full_name.toLowerCase());

      if (distanceA === distanceB) {
        return a.full_name.localeCompare(b.full_name);
      }

      return distanceA - distanceB;
    });
  }, [repos, searchQuery, value]);

  const limitedRepos = useMemo(() => filteredRepos.slice(0, 20), [filteredRepos]);

  const selectedRepo = repos.find((r) => r.id.toString() === value);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (repo: GithubRepo) => {
    onChange(repo.id.toString());
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
  };

  const handleButtonClick = () => {
    if (disabled || isLoading) return;
    if (!isTyping) {
      setIsTyping(true);
      setOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  // Handle popover close
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${
            disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isTyping ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Type here to search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="truncate">
                {isLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading...
                  </span>
                ) : selectedRepo ? (
                  <span className="text-foreground">{selectedRepo.full_name}</span>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{
          width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        {isLoading ? (
          <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading repositories...
          </div>
        ) : (
          <div
            className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
            onWheel={(e) => {
              const container = e.currentTarget;
              const delta = e.deltaY;
              container.scrollTop += delta;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <RepoResultsList
              repos={limitedRepos}
              selectedRepoId={value}
              onSelect={handleSelect}
              emptyMessage={emptyMessage}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
